import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IToastAction } from '@feathr/components';

import { createDuplicateExportErrorHandler } from './GroupExportModal.utils';

export interface IUseDuplicateExportErrorHandlerOptions {
  /**
   * What this surface can change to produce a different export. Each submit path
   * offers different controls, so none of them can share one sentence.
   */
  description: string;
  /**
   * Links the toast at the export that is blocking this one. Omit it on surfaces
   * where the exports dashboard is unreachable, or the link resolves to nothing.
   */
  action?: IToastAction;
}

/**
 * The `onError` an export submit path hands to `useExportModal.handleExport`.
 *
 * A 409 is the one export failure the generic "Couldn't export data. Try again."
 * copy actively misleads on: the backend already has an identical export running
 * for this requester, so retrying cannot clear it. Every other failure keeps the
 * API's own message.
 *
 * @param options.description - Per-surface advice on what to change.
 * @param options.action - Optional toast action; omit where the dashboard is unreachable.
 * @returns The memoised `onError` callback for `IHandleExportOptions`.
 *
 * @example
 * const handleExportError = useDuplicateExportErrorHandler({
 *   description: t('Wait for it to finish before exporting this group again.'),
 *   action: successToastAction,
 * });
 */
export function useDuplicateExportErrorHandler({
  description,
  action,
}: IUseDuplicateExportErrorHandlerOptions): (error: unknown) => void {
  const { t } = useTranslation();
  return useMemo(
    () => createDuplicateExportErrorHandler({ t, description, action }),
    [action, description, t],
  );
}
